import { useState } from "react";
import Cookies from "js-cookie";
import { useForm } from "react-hook-form";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
} from "@pushpress/pushpress-ui";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useAuthenticateUser } from "../api/hooks";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider/AnalyticsProvider";
import { ACCESS_TOKEN, USER_UUID, CLIENT_UUID } from "../constants";
import { cookieAttributes, validEmailRegex } from "../helpers";
type LogInProps = {
  clientUuid: string;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
};

type LoginFormValuesType = {
  email: string;
  password: string;
  hasServerError: boolean;
};

export default function LogIn({ clientUuid, setIsAuthenticated }: LogInProps) {
  const { trackEvent } = useAnalytics();
  const {
    handleSubmit,
    register,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<LoginFormValuesType>({
    mode: "onTouched",
    defaultValues: {
      email: "",
      password: "",
      hasServerError: false,
    },
  });

  const { mutate: postAuthenticateUser } = useAuthenticateUser();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogIn = async (data: LoginFormValuesType) => {
    setIsLoading(true);
    postAuthenticateUser(
      {
        clientUuid,
        email: data.email.trim(),
        password: data.password.trim(),
      },
      {
        onSuccess: (response) => {
          if (response.accessToken && response.userUuid) {
            Cookies.set(ACCESS_TOKEN, response.accessToken, cookieAttributes);
            sessionStorage.setItem(USER_UUID, response.userUuid);
            sessionStorage.setItem(CLIENT_UUID, clientUuid);
            setIsAuthenticated(true);
          } else {
            setError("hasServerError", {
              message: "Invalid email or password",
            });
            trackEvent("Add Payment Incomplete Login Response Error", {
              error: "ap_api_login_error",
              api_error: JSON.stringify(response),
            });
            sessionStorage.clear();
          }
          setIsLoading(false);
        },
        onError: (error) => {
          setError("hasServerError", {
            message: "Invalid email or password",
          });
          trackEvent("Add Payment Login Error", {
            error: "ap_api_login_error",
            api_error: JSON.stringify(error),
          });
          Cookies.remove(ACCESS_TOKEN, cookieAttributes);
          sessionStorage.clear();
          setIsLoading(false);
        },
      },
    );
  };

  return (
    <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-blue-600 tw-p-4">
      <Card className="tw-w-full tw-max-w-md">
        <CardHeader className="tw-space-y-1">
          <CardTitle className="tw-text-2xl tw-font-bold">
            Welcome back
          </CardTitle>
          <CardDescription>
            Enter your email and password to login
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(handleLogIn)} className="tw-space-y-4">
            <div className="tw-space-y-1">
              <Label htmlFor="email">
                Email
                {errors.email && <span className="tw-text-red-500">*</span>}
              </Label>
              <Input
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: validEmailRegex,
                    message: "Valid email is required",
                  },
                  onChange: () => {
                    if (errors.email) {
                      clearErrors("email");
                    }
                  },
                })}
                data-testid="email-input"
                disabled={isLoading}
                className={
                  errors.email
                    ? "tw-border-red-500 tw-focus-visible:tw-ring-red-500"
                    : ""
                }
              />
              <div className="tw-h-4">
                {errors.email && (
                  <div className="tw-text-xs tw-text-red-500">
                    {errors.email.message}
                  </div>
                )}
              </div>
            </div>
            <div className="tw-space-y-1">
              <div className="tw-flex tw-items-center tw-justify-between">
                <Label htmlFor="password">
                  Password
                  {errors.password && (
                    <span className="tw-text-red-500">*</span>
                  )}
                </Label>
              </div>
              <div className="tw-relative">
                <Input
                  {...register("password", {
                    required: "Password is required",
                    onChange: () => {
                      if (errors.password) {
                        clearErrors("password");
                      }
                      if (errors.hasServerError) {
                        clearErrors("hasServerError");
                      }
                    },
                  })}
                  data-testid="password-input"
                  type={showPassword ? "text" : "password"}
                  disabled={isLoading}
                  className={
                    errors.password
                      ? "tw-border-red-500 tw-focus-visible:tw-ring-red-500"
                      : ""
                  }
                />
                <div className="tw-absolute tw-right-2 tw-top-1/2 tw-transform -tw-translate-y-1/2">
                  <Button
                    type="button"
                    variant={null}
                    size="icon"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="tw-h-4 tw-w-4" />
                    ) : (
                      <Eye className="tw-h-4 tw-w-4" />
                    )}
                  </Button>
                </div>
              </div>
              <div className="tw-h-4">
                {errors.password && (
                  <div className="tw-text-xs tw-text-red-500">
                    {errors.password.message}
                  </div>
                )}
                {errors.hasServerError && (
                  <div className="tw-text-xs tw-text-red-500">
                    {errors.hasServerError.message}
                  </div>
                )}
              </div>
            </div>
            <Button type="submit" className="tw-w-full" disabled={isLoading}>
              {isLoading && (
                <Loader2 className="tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin" />
              )}
              Login
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
